import { useCallback, useContext, useEffect } from 'react';

import { getApolloClient } from '@confluence/graphql';
import { usePubSubEvent } from '@confluence/pubsub-client';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';

import { useEditorAnalyticsEvent } from '../useEditorAnalyticsEvent';

import { ContentSpaceKeyQuery } from './ContentSpaceKeyQuery.graphql';
import type {
	ContentSpaceKeyQuery as ContentSpaceKeyQueryType,
	ContentSpaceKeyQueryVariables,
} from './__types__/ContentSpaceKeyQuery';

export const useSpaceKeyWatcher = ({ contentType }: { contentType: string }) => {
	const { match, replace, toUrl } = useContext(RoutesContext);
	const { contentId, spaceKey: spaceKeyFromUrl } = match?.params ?? {};

	const { triggerAnalyticsEvent } = useEditorAnalyticsEvent({
		contentId,
		contentType,
		source: 'editPageScreen',
		subject: 'editor',
		collabService: 'ncs',
	});

	// Replaces the URL to match the given space key
	const changeUrl = useCallback(
		(spaceKey: string) => {
			// Only changing the space key
			const params = {
				...match?.params,
				query: match?.query,
				spaceKey,
			};

			const url = toUrl(match?.name, params);
			if (url !== null) {
				replace(url);
			}
		},
		[replace, toUrl, match],
	);

	// Changes the URL if the new space key from the move event is different from the current
	const handleMoveEvent = useCallback(
		(_: any, payload: any) => {
			const newSpaceAlias = payload?.movePageRequestBody?.newSpaceAlias ?? null;
			if (
				newSpaceAlias !== null &&
				newSpaceAlias?.toLowerCase() !== spaceKeyFromUrl?.toLowerCase()
			) {
				changeUrl(newSpaceAlias);
			}

			// Fire analytics to track when the page or blog was moved
			const eventName = contentType === 'page' ? 'pageMoved' : 'blogMoved';
			void triggerAnalyticsEvent(eventName, {
				editorType: 'next',
				...payload,
			});
		},
		[changeUrl, contentType, spaceKeyFromUrl, triggerAnalyticsEvent],
	);

	// Handles the case where the page is moved while the user is editing it
	usePubSubEvent({
		contentId,
		contentType,
		eventName: 'moved:page',
		onEvent: handleMoveEvent,
	});

	// Handles the case where the user returns to a bookmarked page which has since been moved
	useEffect(() => {
		const fetchSpaceKey = async () => {
			const { data } = await getApolloClient().query<
				ContentSpaceKeyQueryType,
				ContentSpaceKeyQueryVariables
			>({
				query: ContentSpaceKeyQuery,
				variables: {
					contentId,
				},
				fetchPolicy: 'network-only',
			});

			const contentSpaceKeyAsKey = data?.content?.nodes?.[0]?.space?.key ?? null;
			const contentSpaceAliasAsKey = data?.content?.nodes?.[0]?.space?.alias ?? null;
			const contentSpaceKey = contentSpaceAliasAsKey || contentSpaceKeyAsKey;
			if (contentSpaceKey !== null && contentSpaceKey !== spaceKeyFromUrl) {
				changeUrl(contentSpaceKey);
			}
		};

		if (contentId) {
			void fetchSpaceKey();
		}
	}, [contentId, changeUrl, spaceKeyFromUrl]);
};
